import "./infinite_table_component.scss";
import React, { useRef, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

interface InfiniteTableColumn {
  key: string;
  title: string;
  render?: (value: any, row: any, index: number) => React.ReactNode;
  width?: string;
  className?: string;
  responsive?: 'all' | 'desktop' | 'tablet' | 'mobile';
  expandable?: boolean;
  fixed?: boolean;
}

interface InfiniteTableProps {
  data: any[];
  columns: InfiniteTableColumn[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  height?: number | string;
  emptyMessage?: string;
  loadingMessage?: string;
  tableClass?: string;
  rowClass?: string;
  headerClass?: string;
  onRowClick?: (row: any, index: number) => void;
  striped?: boolean;
  hover?: boolean;
  bordered?: boolean;
}

const InfiniteTable: React.FC<InfiniteTableProps> = ({
  data,
  columns,
  loading,
  hasMore,
  onLoadMore,
  height = 570,
  emptyMessage = "No hay datos para mostrar",
  loadingMessage = "Cargando...",
  tableClass = "",
  rowClass = "",
  headerClass = "",
  onRowClick,
  striped = true,
  hover = true,
  bordered = true
}) => {

  const tableRef = useRef<HTMLTableElement>(null);
  const [expandedColumns, setExpandedColumns] = useState<Set<number>>(new Set());

  const toggleColumnExpansion = (columnIndex: number) => {
    setExpandedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnIndex)) {
        newSet.delete(columnIndex);
      } else {
        newSet.add(columnIndex);
      }
      return newSet;
    });
  };

  const handleCellClick = (columnIndex: number, event: React.MouseEvent) => {
    // Solo expandir si la columna es expandible
    if (columns[columnIndex]?.expandable) {
      event.stopPropagation();
      toggleColumnExpansion(columnIndex);
    }
  };

  const getResponsiveClass = (responsive?: string) => {
    switch (responsive) {
      case 'desktop':
        return 'd-none d-lg-table-cell';
      case 'tablet':
        return 'd-none d-md-table-cell';
      case 'mobile':
        return 'd-table-cell d-md-none';
      default:
        return '';
    }
  };

  const tableClasses = [
    'infinite-table',
    'table',
    striped ? 'table-striped' : '',
    hover ? 'table-hover' : '',
    bordered ? 'table-bordered' : '',
    tableClass
  ].filter(Boolean).join(' ');

  const renderCellContent = (column: InfiniteTableColumn, row: any, rowIndex: number) => {
    if (column.render) {
      return column.render(row[column.key], row, rowIndex);
    }
    return row[column.key] || "No disponible";
  };

  return (
    <div className="infinite-table-container">
      <div className={`table-responsive ${expandedColumns.size > 0 ? 'allow-scroll' : ''}`}>
        <InfiniteScroll
          dataLength={data.length}
          next={onLoadMore}
          hasMore={hasMore}
          loader={<></>}
          height={height}
          className={`infinite-scroll-wrapper ${expandedColumns.size > 0 ? 'allow-scroll' : ''}`}
        >
          <table
            ref={tableRef}
            className={`${tableClasses} ${expandedColumns.size > 0 ? 'has-expanded-columns' : ''}`}
          >
            <thead className={`infinite-table-header ${headerClass}`}>
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    className={`${column.className || ''} ${getResponsiveClass(column.responsive)} ${expandedColumns.has(index) ? 'expanded' : ''
                      }`}
                    style={{ width: column.width }}
                  >
                    <div className="header-content">
                      <span className="header-title">{column.title}</span>
                      {column.expandable && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          className={`expand-icon ${expandedColumns.has(index) ? 'rotated' : ''}`}
                        >
                          <path
                            d="M3 4.5L6 7.5L9 4.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && !loading && (
                <tr>
                  <td colSpan={columns.length} className="text-center py-4">
                    <div className="empty-state">
                      <p className="text-muted mb-0">{emptyMessage}</p>
                    </div>
                  </td>
                </tr>
              )}

              {data.length === 0 && loading && (
                <tr>
                  <td colSpan={columns.length} className="text-center py-4">
                    <div className="loading-state">
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span className="text-muted">{loadingMessage}</span>
                    </div>
                  </td>
                </tr>
              )}

              {data.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`infinite-table-row ${rowClass} ${onRowClick ? 'clickable-row' : ''}`}
                  onClick={() => onRowClick && onRowClick(row, rowIndex)}
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className={`${column.className || ''} ${getResponsiveClass(column.responsive)} ${expandedColumns.has(colIndex) ? 'expanded' : ''
                        } ${column.expandable ? 'expandable-cell' : ''}`}
                      onClick={(e) => handleCellClick(colIndex, e)}
                    >
                      {renderCellContent(column, row, rowIndex)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </InfiniteScroll>
      </div>

      {loading && data.length > 0 && (
        <div className="infinite-table-footer">
          <div className="d-flex justify-content-center align-items-center py-3">
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="text-muted">Cargando más datos...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfiniteTable;